import { Context, createContext, FunctionComponent, JSX, ReactNode, useContext, useState } from 'react'
import Notification from '@/components/_custom/Notification/Notification.tsx'
import { AlertProps } from '@mui/material'

interface NotificationContextType {
    show: (message: string, severity?: AlertProps['severity']) => void
    showNotificationSuccess: (message: string) => void
    showNotificationError: (message: string) => void
    open: boolean
    message: string
    severity: AlertProps['severity']
    close: () => void
}

const NotificationContext: Context<NotificationContextType | undefined> = createContext<
    NotificationContextType | undefined
>(undefined)

export const NotificationProvider: FunctionComponent<{ children: ReactNode }> = ({
    children
}: {
    children: ReactNode
}): JSX.Element => {
    const [open, setOpen] = useState(false)
    const [message, setMessage] = useState('')
    const [severity, setSeverity] = useState<AlertProps['severity']>('success')

    const show: (message: string, severity?: AlertProps['severity'] | undefined) => void = (
        msg: string,
        sev: AlertProps['severity'] = 'success'
    ): void => {
        setMessage(msg)
        setSeverity(sev)
        setOpen(true)
    }

    const showNotificationSuccess: (msg: string) => void = (msg: string): void => show(msg, 'success')
    const showNotificationError: (msg: string) => void = (msg: string): void => show(msg, 'error')
    const close: () => void = (): void => setOpen(false)

    return (
        <NotificationContext.Provider
            value={{ show, showNotificationSuccess, showNotificationError, open, message, severity, close }}
        >
            {children}
            <Notification message={message} open={open} setOpen={setOpen} severity={severity} />
        </NotificationContext.Provider>
    )
}

export const useNotification: () => NotificationContextType = (): NotificationContextType => {
    const context: NotificationContextType | undefined = useContext(NotificationContext)

    if (!context) throw new Error('useNotification must be used within NotificationProvider')

    return context
}
